import type {
  EmailComponentTemplateCategory,
  EmailComponentTemplate,
  UserEmailComponentTemplate,
  UserHasSeenServerResponse,
  CategoryPill,
  BlockCategoryMap,
} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import isNumber from 'lodash/isNumber';
import {isUserLoggedIn} from '@Libraries/user.library';
import type {Block} from 'grapesjs';
import type {Deferred} from '@Libraries/deferred';
import {pollForUpdatesInURL} from '@Libraries/poster-library';

export const getAllCategoriesPill = (): CategoryPill => {
  return {
    id: 0,
    name: window.i18next.t('pmwjs_all'),
    trackingName: '',
  };
};

export const hasUserSeenSectionsBlock = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return false;
  }

  const data = (await window.PMW.readLocal(`emailcomponenttemplate/hasUserSeenEmailMakerSectionsBlock`)) as UserHasSeenServerResponse;
  data.hasSeen = !isNumber(data.hasSeen) ? parseInt(data.hasSeen, 10) : data.hasSeen;
  return data.hasSeen === 1;
};

export const hasUserSeenElementsBlock = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return false;
  }

  const data = (await window.PMW.readLocal(`emailcomponenttemplate/hasUserSeenEmailMakerElementsBlock`)) as UserHasSeenServerResponse;
  data.hasSeen = !isNumber(data.hasSeen) ? parseInt(data.hasSeen, 10) : data.hasSeen;
  return data.hasSeen === 1;
};

export const markUserHasSeenSectionsBlock = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }

  await window.PMW.writeLocal(`emailcomponenttemplate/markUserHasSeenEmailMakerSectionsBlock`);
};

export const markUserHasSeenElementsBlock = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }

  await window.PMW.writeLocal(`emailcomponenttemplate/markUserHasSeenEmailMakerElementsBlock`);
};

export const getAllEmailComponentTemplateCategories = async (): Promise<EmailComponentTemplateCategory[]> => {
  return (await window.PMW.readLocal(`emailcomponenttemplate/getAllCategories`)) as EmailComponentTemplateCategory[];
};

export const getAllEmailComponentTemplates = async (): Promise<EmailComponentTemplate[]> => {
  return (await window.PMW.readLocal(`emailcomponenttemplate/getAllTemplates`)) as EmailComponentTemplate[];
};

export const getUserEmailComponentTemplates = async (): Promise<UserEmailComponentTemplate[]> => {
  if (!isUserLoggedIn()) {
    return [];
  }

  return (await window.PMW.readLocal(`emailcomponenttemplate/getUserTemplates`)) as UserEmailComponentTemplate[];
};

export const deleteUserEmailComponentTemplate = (templateId: number): void => {
  if (!isUserLoggedIn()) {
    return;
  }

  void window.PMW.writeLocal(`emailcomponenttemplate/deleteUserTemplate`, {templateId});
};

export const updateUserEmailComponentTemplateName = (templateId: number, newName: string): void => {
  if (!isUserLoggedIn()) {
    return;
  }

  void window.PMW.writeLocal(`emailcomponenttemplate/updateUserTemplateName`, {templateId, newName});
};

export const pollForComponentTemplatePreviewChanges = (url: string): Deferred => {
  return pollForUpdatesInURL(url);
};

export function getBlocksFromCategories(blocksMap: BlockCategoryMap, categories: string[]): Block[] {
  return categories.reduce((result: Block[], category: string) => {
    return result.concat(blocksMap[category]);
  }, []);
}

export function getIconFromBlockId(blockId: string): string {
  switch (blockId) {
    case window.PMW.emails.blocks.ID_TABLE:
      return 'icon-table';
    case window.PMW.emails.blocks.ID_STICKER:
      return 'icon-sticker';
    case window.PMW.emails.blocks.ID_ICONS:
      return 'icon-icons';
    case window.PMW.emails.blocks.ID_VIDEO:
      return 'icon-video';
    case window.PMW.emails.blocks.ID_BUTTON:
      return 'icon-button';
    case window.PMW.emails.blocks.ID_SOCIAL:
      return 'icon-social-media';
    case window.PMW.emails.blocks.ID_DIVIDER:
      return 'icon-divider';
    case window.PMW.emails.blocks.ID_UPLOAD_CONTENT:
      return 'icon-upload';
    case window.PMW.emails.blocks.ID_EXPLORE_MEDIA:
      return 'icon-media';
    case window.PMW.emails.blocks.ID_AI_IMAGES:
      return 'icon-ai-image';
    case window.PMW.emails.blocks.ID_DESIGN:
      return 'icon-designs';
    case window.PMW.emails.blocks.CATEGORY_TEXT:
      return 'icon-text';
    case window.PMW.emails.blocks.CATEGORY_IMAGE:
      return 'icon-image';
    default:
      return 'icon-square';
  }
}
